import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore'
import { useSessionStore } from '../state'
import { getDiveById } from '../services/diveService'
import * as joinRequestService from '../services/joinRequestService'
import PendingRequestCard from '../components/PendingRequestCard'
import BackfillPrompt from '../components/BackfillPrompt'
import Database from '../types/Database'
import routes from './routes'

const ApprovalScreen = () => {
  const { id: diveId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const user = useSessionStore(state => state.user)

  const [dive, setDive] = useState<Database.Dive | null>(null)
  const [requests, setRequests] = useState<Database.PendingRequestView[]>([])
  const [approvedCount, setApprovedCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showBackfill, setShowBackfill] = useState(false)

  const capacityCap = dive ? dive.maxParticipants : 0

  const fetchData = useCallback(async () => {
    if (!diveId) {
      setError('Dive ID is missing')
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const diveData = await getDiveById(diveId)

      if (!diveData) {
        setError('Dive not found')
        setIsLoading(false)
        return
      }

      // Verify current user is the host
      if (!user || diveData.hostId !== user.uid) {
        navigate(routes.home, { replace: true })
        return
      }

      setDive(diveData)

      // Fetch all dive requests (pending, approved, declined)
      const allRequests = await joinRequestService.getDiveRequests(diveId)
      setRequests(allRequests)

      // Query approved count for this dive
      const db = getFirestore()
      const joinRequestsRef = collection(db, 'joinRequests')
      const approvedQuery = query(
        joinRequestsRef,
        where('diveId', '==', diveId),
        where('status', '==', 'approved')
      )
      const approvedSnap = await getDocs(approvedQuery)
      setApprovedCount(approvedSnap.size)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load requests'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [diveId, user, navigate])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleActionComplete = () => {
    fetchData()
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity='error'
          action={
            <Button color='inherit' size='small' onClick={fetchData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    )
  }

  // Sort: pending first, then approved, then declined
  const statusOrder: Record<Database.JoinRequestStatus, number> = {
    pending: 0,
    approved: 1,
    declined: 2,
    forfeited: 3,
    cancelled: 4
  }
  const sortedRequests = [...requests].sort(
    (a, b) => statusOrder[a.status] - statusOrder[b.status]
  )

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant='h5' component='h1'>
            Dive Requests
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {approvedCount} / {capacityCap} spots filled
          </Typography>
        </Box>

        {showBackfill && dive && (
          <BackfillPrompt
            diveId={dive.id}
            forfeitedCount={1}
            onComplete={() => {
              setShowBackfill(false)
              fetchData()
            }}
          />
        )}

        {sortedRequests.length === 0 ? (
          <Alert severity='info'>No requests yet</Alert>
        ) : (
          <Stack spacing={2}>
            {sortedRequests.map(request => (
              <PendingRequestCard
                key={request.requestId}
                request={request}
                isAtCapacity={approvedCount >= capacityCap}
                onActionComplete={handleActionComplete}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  )
}

export default ApprovalScreen
